import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth/auth-context";
import {
  fetchFeedbackPromptEligibility,
  isFeedbackPromptDeferredThisSession,
  markFeedbackPromptDeferredThisSession,
  setFeedbackPromptStatus,
} from "../lib/feedback/prompt";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

// Sollicitation unique « Comment ça se passe ? » (item C) — une seule carte,
// refusable définitivement (« Non merci ») ou reportable (« Plus tard »).
// L'admissibilité (≥ 3 captures, statut NULL/later) se lit en base au montage ;
// rien ne s'affiche tant qu'elle n'est pas confirmée (fail-closed : rater une
// sollicitation est bénin, la re-poser à qui a refusé ne l'est pas).

const SET_STATUS_ERROR = "Impossible d'enregistrer votre choix. Réessayez dans un instant.";

export function FeedbackPromptCard() {
  const { client, user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId || isFeedbackPromptDeferredThisSession()) return;
    let alive = true;
    fetchFeedbackPromptEligibility({ client, userId }).then(
      (r) => {
        if (alive && r.ok && r.eligible) setVisible(true);
      },
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [client, userId]);

  if (!visible || !userId) return null;

  async function dismiss(status: "later" | "declined") {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const r = await setFeedbackPromptStatus({ client, userId, status });
    setBusy(false);
    if (!r.ok) {
      // La carte reste : un « plus jamais » local qui n'a pas été écrit en
      // base divergerait des autres appareils.
      setError(SET_STATUS_ERROR);
      return;
    }
    if (status === "later") markFeedbackPromptDeferredThisSession();
    setVisible(false);
  }

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <p className="m-0 text-body font-semibold">Comment ça se passe ?</p>
        <p className="m-0 text-small text-muted">
          3 questions, 2 minutes — dites-nous où vous en êtes.
        </p>
      </div>
      {error && <p className="m-0 text-small text-red-deep">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={busy}
          onClick={() => navigate("/retroaction?source=sollicite")}
        >
          Répondre
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void dismiss("later")}>
          Plus tard
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void dismiss("declined")}>
          Non merci
        </Button>
      </div>
    </Card>
  );
}
